import type { ProjectGraphNode, ProjectGraphResponse } from '@avisoma/shared';
import type { DocumentNode } from './graphAudit';
import { indexOf } from './graphIndex';
import { nodeLabel } from './nodeLabel';

export type CoMentionCaps = { rows: number; columns: number };

/** The matrix shows at most this many documents and co-mentioned entities until the user asks for more. */
export const CO_MENTION_CAPS: CoMentionCaps = { rows: 15, columns: 12 };

/**
 * The most "Show all" shows: a hub entity of a large project would otherwise draw millions of cells and freeze the
 * tab.
 */
export const CO_MENTION_CEILING: CoMentionCaps = { rows: 200, columns: 100 };

export type CoMentionMatrix = {
  /** The documents that mention the entity, those that mention the most other entities first. */
  rows: DocumentNode[];
  /** The other entities those documents mention, with the count of rows (of all, not only the shown ones) that do. */
  columns: { node: ProjectGraphNode; count: number }[];
  /** `cells[r][c]`: the linked pages of the edge from `rows[r]` to `columns[c]`; `null` when there is no edge. */
  cells: (number | null)[][];
  /** The counts before the caps. */
  totalRows: number;
  totalColumns: number;
};

const collator = new Intl.Collator('en', { numeric: true, sensitivity: 'base' });

/**
 * The documents that mention `entityId` against the other entities they mention: a co-mention matrix. The entity's
 * own column is left out. `caps` cuts the rows and columns to the first ones.
 */
export function coMentionMatrix(
  graph: ProjectGraphResponse,
  entityId: string,
  caps: CoMentionCaps,
): CoMentionMatrix {
  const { nodesById, incoming, outgoing } = indexOf(graph);
  // Each mentioning document, with the linked pages it records for each other entity it mentions.
  const pagesByDocument = new Map<string, Map<string, Set<string>>>();
  for (const edge of incoming.get(entityId) ?? []) {
    const document = nodesById.get(edge.from);
    if (document?.kind !== 'document' || pagesByDocument.has(document.id)) continue;
    const pages = new Map<string, Set<string>>();
    for (const other of outgoing.get(document.id) ?? []) {
      const target = nodesById.get(other.to);
      if (!target || target.id === entityId || target.kind === 'document') continue;
      const set = pages.get(target.id) ?? new Set<string>();
      other.sources.forEach((s) => s.pageIds.forEach((p) => set.add(p)));
      pages.set(target.id, set);
    }
    pagesByDocument.set(document.id, pages);
  }

  const counts = new Map<string, number>();
  for (const pages of pagesByDocument.values())
    for (const id of pages.keys()) counts.set(id, (counts.get(id) ?? 0) + 1);
  const label = (id: string) => {
    const node = nodesById.get(id);
    return node ? nodeLabel(node) : id;
  };
  const byLabel = (a: string, b: string) => collator.compare(label(a), label(b)) || (a < b ? -1 : a > b ? 1 : 0);
  const columnIds = [...counts.keys()].sort((a, b) => (counts.get(b) ?? 0) - (counts.get(a) ?? 0) || byLabel(a, b));
  const rowIds = [...pagesByDocument.keys()].sort(
    (a, b) => (pagesByDocument.get(b)?.size ?? 0) - (pagesByDocument.get(a)?.size ?? 0) || byLabel(a, b),
  );

  const shownRows = rowIds.slice(0, caps.rows);
  const shownColumns = columnIds.slice(0, caps.columns);
  return {
    rows: shownRows.flatMap((id) => {
      const node = nodesById.get(id);
      return node?.kind === 'document' ? [node] : [];
    }),
    columns: shownColumns.flatMap((id) => {
      const node = nodesById.get(id);
      return node ? [{ node, count: counts.get(id) ?? 0 }] : [];
    }),
    cells: shownRows.map((row) => shownColumns.map((column) => pagesByDocument.get(row)?.get(column)?.size ?? null)),
    totalRows: rowIds.length,
    totalColumns: columnIds.length,
  };
}
